import classNames from "classnames";
import { InputText } from "primereact/inputtext";
import React, { Component } from "react";
import { Col, Label, FormGroup, Row } from "reactstrap";
import { AppCard, StatusBadge, createTabHeaderTemplate, LoadingDots, PageLayout } from "../../components/ui";
import {
    BoricaKycTypes,
    KycTypes,
    Merchant,
    MerchantConfigurationModel,
    MerchantStatus,
    ToggleBtrustAppAllowedRequest,
    TogglePepFormSubmitRequest,
    UpdateKycTypeRequest,
} from "../../models/MerchantModel";
import MerchantTariffs from "./MerchantTariffs";
import MerchantGroups from "./MerchantGroups";
import MerchantContact from "./MerchantContact";
import MerchantPaymentMethods from "./MerchantPaymentMethods";
import MerchantUsers from "./MerchantUsers";
import { Dropdown } from "primereact/dropdown";
import { SelectItem } from "../../models/SelectItem";
import { Button } from "primereact/button";
import history from "../../history";
import { confirmDanger, confirmAction } from "../../utils/confirmHelpers";
import MerchantDocuments from "./MerchantDocuments";
import { Result } from "../../models/Result";
import { toastService } from "../../services/toastService";
import _ from "lodash";
import { merchantApiService } from "../../services/api/merchants/MerchantApiService";
import { httpService } from "../../services/HttpService";
import ExtendedInputText from "../../components/ExtendedInputText";
import StringUtils from "../../utils/stringUtils";
import MerchantAddress from "./MerchantAddress";
import { TabView, TabPanel } from "primereact/tabview";
import { utils } from "../../utils/utils";
import { fileService } from "../../services/FileService";
import { ProfileImage } from "../../components/Image/ProfileImage";
import { canDeleteMerchant, isMerchantProfileUpdateAllowed, isMerchantsClientsCreateAllowed, isSuperAdmin } from "../../authGuard";
import MerchantIntegration from "./MerchantIntegration";
import { clientApiService } from "../../services/api/clients/ClientApiService";
import { CurrencyModel } from "../../models/CurrencyModel";
import { MultiSelect } from "primereact/multiselect";
import { currenciesApiService } from "../../services/api/merchants/CurrenciesApiService";
import MerchantWalletsComponent, { WalletType } from "./Wallets";
import MerchantDefaultPaymentMethod from "./MerchantDefaultPaymentMethod";
import MerchantTransactions from "./MerchantTransactions";
import { storageService } from "../../services/StorageService";
import { currencies } from "./CreateMerchant";
import ScaSectionsComponent from "./ScaSection";
import { Check, CheckCircle, Users, XCircle } from "lucide-react";
import { IndividualStatus } from "../../models/User";
import MerchantClientComponent from "./MerchantClient";
import MerchantVendors from "./MerchantVendors/MerchantVendors";
import { InputNumber } from "primereact/inputnumber";
import MerchantConfigration from "./MerchantConfigration";
import { merchantConfigrationsApiService } from "../../services/api/merchants/MerchantConfigrations";
import { AppConstants, Image_File_Size } from "../../models/AppConstants";

interface MerchantDetailsState {
    isLoading: boolean;
    merchant?: Merchant | null;
    merchantPass?: Merchant | null;
    merchantId?: number;
    merchantStatusEdit: boolean;
    selectedCountry?: SelectItem | null;
    activeTabIndex: number;
    countries: SelectItem[];
    currencies: CurrencyModel[];
    merchantClients: SelectItem[];
    isExternalKyc: boolean;
    isBtrustAppActive: boolean;
    isNewRegistrationActive: boolean;
    externalKycLoader: boolean;
    allowMultiMerchantIban: boolean;
    allowMultiMerchantClientIban: boolean;
    isKycOptional: boolean;
    isPepDeclaration: boolean;
    isBillTariffActive: boolean;
}

export default class MerchantDetailsComponent extends Component<Record<string, never>, MerchantDetailsState> {
    validationErrors: Map<string, boolean> = new Map<string, boolean>();

    // TODO. Fix this
    merchantStatus: SelectItem[] = Object.keys(MerchantStatus)
        .map((key) => MerchantStatus[key as any])
        .filter((f) => typeof f == "string")
        .map((val) => ({ label: val, key: val }) as any);

    constructor(props: Record<string, never>) {
        super(props);

        this.state = {
            merchantId: utils.getIdentifierFromPath(),
            merchant: null,
            activeTabIndex: 0,
            isLoading: true,
            merchantStatusEdit: false,
            countries: [],
            currencies: [],
            merchantClients: [],
            isExternalKyc: false,
            isBtrustAppActive: false,
            isNewRegistrationActive: false,
            externalKycLoader: false,
            allowMultiMerchantIban: false,
            allowMultiMerchantClientIban: false,
            isKycOptional: false,
            isPepDeclaration: false,
            isBillTariffActive: false,
            isCardIssuer: false,
        };
    }

    async componentDidMount() {
        const countriesResult = await utils.getCountriesResult();
        if (!countriesResult.success) {
            toastService.error(countriesResult.message);
            this.setState({ isLoading: false });
            return;
        }

        const countries = utils.getCountries();

        const clientsResult = await clientApiService.getClients();
        if (!clientsResult.success) {
            this.setState({ isLoading: false });
            toastService.error(clientsResult.message);
            return;
        }

        const currenciesResult = await currenciesApiService.getActive();

        if (!currenciesResult.success) {
            this.setState({ isLoading: false });
            toastService.error(currenciesResult.message);
            return;
        }

        const currencies = currenciesResult.data.map((m: CurrencyModel) => ({ name: m.charCode, id: m.id }) as CurrencyModel);

        const merchantResult =
            this.state.merchantId == 0
                ? ({ success: true, data: new Merchant() } as Result<Merchant>)
                : await merchantApiService.get(this.state.merchantId!);
        if (!merchantResult.success) {
            this.setState({ isLoading: false });
            toastService.error(merchantResult.message);
            return;
        }
        storageService.setSelectedMerchantId(merchantResult.data.id.toString());
        this.setState(
            {
                isKycOptional: merchantResult.data.isKycOptional != null ? merchantResult.data.isKycOptional === AppConstants.True : false,

                allowMultiMerchantIban:
                    merchantResult.data.allowMultiMerchantIban != null ? merchantResult.data.allowMultiMerchantIban === AppConstants.True : false,

                allowMultiMerchantClientIban:
                    merchantResult.data.allowMultiMerchantClientIban != null
                        ? merchantResult.data.allowMultiMerchantClientIban === AppConstants.True
                        : false,
                isExternalKyc: merchantResult.data.kycType == KycTypes.External,
                isBtrustAppActive:
                    merchantResult.data.boricaKycType != null
                        ? merchantResult.data.boricaKycType.includes(BoricaKycTypes.BTrustApp) ||
                        merchantResult.data.boricaKycType.includes(BoricaKycTypes.AppAndNewRegistration)
                        : false,
                isNewRegistrationActive:
                    merchantResult.data.boricaKycType != null
                        ? merchantResult.data.boricaKycType.includes(BoricaKycTypes.NewRegistration) ||
                        merchantResult.data.boricaKycType.includes(BoricaKycTypes.AppAndNewRegistration)
                        : false,
                isPepDeclaration: merchantResult.data.isPepDeclaration != null ? merchantResult.data.isPepDeclaration === AppConstants.True : false,
                isBillTariffActive: merchantResult.data.isBillTariffActive != null ? merchantResult.data.isBillTariffActive === AppConstants.True : false,
                isCardIssuer: merchantResult.data.isCardIssuer != null ? merchantResult.data.isCardIssuer === AppConstants.True : false,
                merchant: merchantResult.data,
                merchantClients: clientsResult.data,
                currencies: currencies,
                countries: countries,
                merchantPass: merchantResult.data,
                isLoading: false,
            },
            async () => {
                await this.loadImage();
            },
        );
    }

    loadImage = async () => {
        if (this.state.merchant?.logo == null || this.state.merchant?.logo.length == 0) {
            return;
        }

        const logoResult = await fileService.GetFile(this.state.merchant?.logo);

        if (!logoResult.success) {
            toastService.error("Could not load Merchant Logo. Please contact your administrator");
            return;
        }

        const merchant = this.state.merchant;
        merchant.logo = logoResult.data;

        this.setState({
            merchant: merchant,
        });
    };

    updateValue = (fieldName: string, value: string) => {
        const merchantDetails = this.state.merchant as any;

        merchantDetails[fieldName] = value;

        this.setState({ merchant: merchantDetails });
    };

    onUpdateCurrencyMultiSelect = (ids: number[]) => {
        if (this.state.merchant != null) {
            const merchant = this.state.merchant;
            if (ids.includes(currencies.AAA)) {
                // Select all currency IDs

                merchant.merchantCurrencies = this.state.currencies
                    .filter((currency) => parseInt(currency.id) !== currencies.AAA)
                    .map((currency) => parseInt(currency.id));
            } else {
                merchant.merchantCurrencies = ids;
            }
            this.setState({ merchant: merchant });
        }
    };

    onImageSelected = async (image: File) => {
        const merchantDetails = this.state.merchant;
        const allowedTypes = ["image/jpeg", "image/png"];
        if (!allowedTypes.includes(image.type)) {
            toastService.error("Only JPEG and PNG files are allowed.", "File Type");
            return;
        }

        if (image.size > Image_File_Size) {
            toastService.error(`Files less than ${Image_File_Size / (1024 * 1024)}MB are supported only.`, "File Size");
            return;
        }
        merchantDetails!.logo = (await utils.convertFileToBase64(image)) as string;
        this.setState({ merchant: merchantDetails });
    };

    fileInput = (input: boolean) => {
        if (!input) {
            toastService.error("File Name Exceeds 128 Characters.", "File Name");
            return;
        }
    };

    onSubmitButtonClick = async () => {
        this.setState({ isLoading: true });

        const result =
            this.state.merchant?.id == 0
                ? await merchantApiService.create(this.state.merchant)
                : await merchantApiService.update(this.state.merchant!);

        this.setState({ isLoading: false });

        if (result.success) {
            toastService.success(result.message);
            //history.push("/merchants");
        } else {
            toastService.error(result.message);
        }
    };
    renderStatusChangeButton = () => {
        if (!canDeleteMerchant()) {
            return <span></span>;
        }

        return (
            <button type="button" className="inline-flex items-center justify-center rounded-md p-2 hover:bg-neutral-100 transition-colors" tabIndex={0} onClick={this.onStatusChangeButtonClick}>
                {this.state.merchant?.status == MerchantStatus.Active ? (
                    <XCircle size={16} className="cursor-pointer text-neutral-500 hover:text-danger-500 transition-colors" />
                ) : (
                    <CheckCircle size={16} className="cursor-pointer text-neutral-500 hover:text-primary-500 transition-colors" />
                )}
            </button>
        );
    };

    onStatusChangeButtonClick = () => {
        if (this.state.merchant?.status == MerchantStatus.Active) {
            confirmDanger(
                "This action will deactivate all users related to this Merchant. Are you sure you want to proceed?",
                "Deactivate Merchant",
                () => this.deactivateMerchant(),
            );
        } else {
            confirmAction(
                "Are you sure you want to proceed?",
                "Activate Merchant",
                () => this.activateMerchant(),
            );
        }
    };

    setMerchantStatus = (status: MerchantStatus) => {
        const merchant = this.state.merchant;
        merchant!.status = status;
        this.setState({ merchant: merchant });
    };

    deactivateMerchant = async () => {
        if (this.state.merchant == null) {
            return;
        }

        await merchantApiService.deActivate(this.state.merchant.id);
        this.setMerchantStatus(MerchantStatus.InActive);
    };

    activateMerchant = async () => {
        if (this.state.merchant == null) {
            return;
        }

        await merchantApiService.activate(this.state.merchant.id);
        this.setMerchantStatus(MerchantStatus.Active);
    };

    defaultImage = () => {
        return "";
    };

    onChangePepDeclarationSwitch = async (isPepDeclaration: boolean) => {
        const request: TogglePepFormSubmitRequest = {
            isPepDeclaration: isPepDeclaration ? AppConstants.True : AppConstants.False,
        };
        const result = await merchantConfigrationsApiService.togglePepFormSubmit(request, this.state.merchantId!);
        if (!result.success) {
            toastService.error(result.message);
            return;
        }

        this.setState({
            isPepDeclaration: result.data != null && result.data.value === AppConstants.True ? true : false
        });
        return;
    };

    onChangeKycSwitch = async (value: boolean) => {
        const kycType = value ? KycTypes.External : KycTypes.Internal;
        const request: UpdateKycTypeRequest = {
            kycType: kycType,
        };
        const result = await merchantConfigrationsApiService.updateMerchantKycType(this.state.merchantId!, request);
        if (!result.success) {
            toastService.error(result.message);
            return;
        }

        if (value) {
            this.setState({
                isExternalKyc: value,
                activeTabIndex: 10,
                isBtrustAppActive:
                    result.data != null
                        ? result.data.includes(BoricaKycTypes.BTrustApp) || result.data.includes(BoricaKycTypes.AppAndNewRegistration)
                        : false,
                isNewRegistrationActive:
                    result.data != null
                        ? result.data.includes(BoricaKycTypes.NewRegistration) || result.data.includes(BoricaKycTypes.AppAndNewRegistration)
                        : false,
            });
            return;
        }
        window.location.reload();
    };

    onBtrustAppSwitchToggle = async (value: boolean) => {
        const request: ToggleBtrustAppAllowedRequest = {
            boricaKycType: value
                ? this.state.isNewRegistrationActive
                    ? BoricaKycTypes.AppAndNewRegistration
                    : BoricaKycTypes.BTrustApp
                : BoricaKycTypes.NewRegistration,
        };
        const result = await merchantConfigrationsApiService.toggleBtrustAppActivation(request, this.state.merchantId!);
        if (!result.success) {
            toastService.error(result.message);
            return;
        }

        this.setState({
            isBtrustAppActive:
                result.data != null
                    ? result.data.value == BoricaKycTypes.BTrustApp || result.data.value == BoricaKycTypes.AppAndNewRegistration
                    : false,
            isNewRegistrationActive:
                result.data != null
                    ? result.data.value == BoricaKycTypes.NewRegistration || result.data.value == BoricaKycTypes.AppAndNewRegistration
                    : false,
        });
        return;
    };

    onToggleMerchantMultiIbanAsync = async (allowMultiIban: boolean) => {
        const request: MerchantConfigurationModel = {
            key: AppConstants.AllowMultiMerchantIban,
            value: allowMultiIban ? AppConstants.True : AppConstants.False,
            description: "Get Or Set Value for Merchant Multi Iban",
            configrationType: "Merchant Multi Iban",
        };
        const result = await merchantConfigrationsApiService.setMerchantConfigurationAsync(request, this.state.merchantId!);
        if (!result.success) {
            toastService.error(result.message);
            return;
        }
        this.setState({
            allowMultiMerchantIban: result.data != null && result.data.value === AppConstants.True ? true : false,
        });
        return;
    };
    onToggleMerchantClientMultiIbanAsync = async (allowMultiClientIban: boolean) => {
        const request: MerchantConfigurationModel = {
            key: AppConstants.AllowMultiMerchantClientIban,
            value: allowMultiClientIban ? AppConstants.True : AppConstants.False,
            description: "Get Or Set Value for Merchant Client Multi Iban",
            configrationType: "Merchant Multi Iban for Client",
        };
        const result = await merchantConfigrationsApiService.setMerchantConfigurationAsync(request, this.state.merchantId!);
        if (!result.success) {
            toastService.error(result.message);
            return;
        }

        this.setState({
            allowMultiMerchantClientIban: result.data != null && result.data.value === AppConstants.True ? true : false,
        });
        return;
    };
    onToggleisKycOptionalAsync = async (isKycOptional: boolean) => {
        const request: MerchantConfigurationModel = {
            key: AppConstants.IsKycOptional,
            value: isKycOptional ? AppConstants.True : AppConstants.False,
            description: "Get Or Set Value for Optional KYC After Change this kyc bypass and user can see dashboard",
            configrationType: "IsKycOptional for Client",
        };
        const result = await merchantConfigrationsApiService.setMerchantConfigurationAsync(request, this.state.merchantId!);
        if (!result.success) {
            toastService.error(result.message);
            return;
        }

        this.setState({
            isKycOptional: result.data != null && result.data.value === AppConstants.True ? true : false,
        });
        return;
    };
    onToggleBillTariffActiveAsync = async (isBillTariffActive: boolean) => {
        const request: MerchantConfigurationModel = {
            key: AppConstants.IsBillTariffActive,
            value: isBillTariffActive ? AppConstants.True : AppConstants.False,
            description: "Get Or Set Value for Bill Tariff Active",
            configrationType: "Bill Tariff Active",
        };
        const result = await merchantConfigrationsApiService.setMerchantConfigurationAsync(request, this.state.merchantId!);
        if (!result.success) {
            toastService.error(result.message);
            return;
        }
        this.setState({
            isBillTariffActive: result.data != null && result.data.value === AppConstants.True ? true : false,
        });
        return;
    };
    onToggleCardIssuerAsync = async (isCardIssuer: boolean) => {
        const request: MerchantConfigurationModel = {
            key: AppConstants.IsCardIssuer,
            value: isCardIssuer ? AppConstants.True : AppConstants.False,
            description: "Indicates if merchant is a card issuer",
            configrationType: "Card Issuer",
        };
        const result = await merchantConfigrationsApiService.setMerchantConfigurationAsync(request, this.state.merchantId!);
        if (!result.success) {
            toastService.error(result.message);
            return;
        }
        this.setState({
            isCardIssuer: result.data != null && result.data.value === AppConstants.True ? true : false,
        });
        return;
    };
    onNewRegistrationSwitchToggle = async (value: boolean) => {
        const request: ToggleBtrustAppAllowedRequest = {
            boricaKycType: value
                ? this.state.isBtrustAppActive
                    ? BoricaKycTypes.AppAndNewRegistration
                    : BoricaKycTypes.NewRegistration
                : BoricaKycTypes.BTrustApp,
        };
        const result = await merchantConfigrationsApiService.toggleBtrustAppActivation(request, this.state.merchantId!);
        if (!result.success) {
            toastService.error(result.message);
            return;
        }

        this.setState({
            isBtrustAppActive:
                result.data != null
                    ? result.data.value == BoricaKycTypes.BTrustApp || result.data.value == BoricaKycTypes.AppAndNewRegistration
                    : false,
            isNewRegistrationActive:
                result.data != null
                    ? result.data.value == BoricaKycTypes.NewRegistration || result.data.value == BoricaKycTypes.AppAndNewRegistration
                    : false,
        });
        return;
    };

    renderMerchantBody = () => {
        if (this.state.isLoading || !this.state.merchant) {
            return (
                <AppCard className="min-h-[500px] flex items-center justify-center">
                    <LoadingDots />
                </AppCard>
            );
        }

        return (
            <AppCard>
                    <AppCard.Header title="Merchant Details" />
                    <AppCard.Body>
                        <Row>
                            <Col md="3" lg="3" sm="12" className="p-8">
                                <Row style={{ cursor: "pointer" }}>
                                    <ProfileImage
                                        imageStyle={{ width: "100%" }}
                                        className="rounded-circle"
                                        src={
                                            this.state.merchant?.logo == null || this.state.merchant?.logo.length === 0
                                                ? this.defaultImage()
                                                : utils.appendBase64Data(this.state.merchant!.logo)
                                        }
                                        alt="merchant logo"
                                        onImageSelected={this.onImageSelected}
                                        fileInput={this.fileInput}
                                    />
                                </Row>
                                <Row style={{ marginTop: "50px" }}>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>
                                                <b>Status</b>
                                            </Label>
                                        </FormGroup>
                                    </Col>
                                    <Col md={3} style={{ padding: "0 10px" }}>
                                        {this.state.merchantStatusEdit ? (
                                            <Dropdown
                                                style={{ width: "100%" }}
                                                value={MerchantStatus[this.state.merchant?.status ?? 0]}
                                                options={this.merchantStatus}
                                                optionLabel="label"
                                                optionValue="value"
                                                onChange={(e) => this.updateValue("status", e.value)}
                                                itemTemplate={(option) => {
                                                    return <span>{option.label}</span>;
                                                }}
                                            />
                                        ) : (
                                            <div
                                                className={classNames(
                                                    "badge",
                                                    this.state.merchant?.status == MerchantStatus.Active ? "badge-success" : "badge-danger",
                                                )}
                                            >
                                                {MerchantStatus[this.state.merchant?.status ?? 0]}
                                            </div>
                                        )}
                                    </Col>
                                    <Col md={3}>{this.renderStatusChangeButton()}</Col>
                                </Row>
                            </Col>
                            <Col md={9} lg={9} sm="12">
                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Name</Label>
                                            <ExtendedInputText
                                                name="Name"
                                                value={this.state.merchant?.name}
                                                required={true}
                                                readOnly={!isMerchantProfileUpdateAllowed()}
                                                onChange={(value) => this.updateValue("name", value)}
                                                onValidStatusChanged={(value: boolean) => this.validationErrors.set("name", value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Email Address</Label>
                                            <ExtendedInputText
                                                type="email"
                                                value={this.state.merchant?.email}
                                                onChange={(value) => this.updateValue("email", value)}
                                                validation={() => StringUtils.isValidEmail(this.state.merchant?.email ?? "")}
                                                readOnly={!isMerchantProfileUpdateAllowed()}
                                                name="Email"
                                                required={true}
                                                onValidStatusChanged={(value: boolean) => this.validationErrors.set("email", value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Website Url</Label>
                                            <InputText
                                                value={this.state.merchant?.websiteUrl}
                                                readOnly={!isMerchantProfileUpdateAllowed()}
                                                className={"form-control"}
                                                onChange={(e) => this.updateValue("websiteUrl", e.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Phone</Label>
                                            <InputText
                                                value={this.state.merchant?.phone}
                                                readOnly={!isMerchantProfileUpdateAllowed()}
                                                className={"form-control"}
                                                type="text"
                                                onChange={(e) => this.updateValue("phone", e.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>VAT Number</Label>
                                            <InputText
                                                value={this.state.merchant?.vatNumber}
                                                readOnly={!isMerchantProfileUpdateAllowed()}
                                                className={"form-control"}
                                                type="text"
                                                onChange={(e) => this.updateValue("vatNumber", e.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <Label>Currency</Label>
                                        <FormGroup>
                                            <MultiSelect
                                                value={this.state.merchant?.merchantCurrencies}
                                                disabled={!isMerchantProfileUpdateAllowed()}
                                                display="chip"
                                                options={this.state.currencies}
                                                onChange={(e) => this.onUpdateCurrencyMultiSelect(e.value)}
                                                optionLabel="name"
                                                optionValue="id"
                                                placeholder="Select Currency"
                                                filter
                                                className="multiselect-custom"
                                                style={{ width: "100%" }}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Flexcube Branch</Label>
                                            <InputText
                                                value={this.state.merchant?.flexCubeBranch}
                                                readOnly={!isMerchantProfileUpdateAllowed()}
                                                className={"form-control"}
                                                type="text"
                                                disabled={true}
                                                onChange={(e) => this.updateValue("flexCubeBranch", e.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <Label>Merchant Client</Label>
                                        <FormGroup>
                                            <MultiSelect
                                                value={this.state.merchant?.clientIds}
                                                disabled={true}
                                                options={this.state.merchantClients}
                                                display="chip"
                                                optionLabel="label"
                                                optionValue="value"
                                                placeholder={"Select Merchant Client"}
                                                itemTemplate={(option) => {
                                                    return <span>{option.label}</span>;
                                                }}
                                                style={{ width: "100%" }}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>BulStat</Label>
                                            <InputText
                                                disabled={true}
                                                value={this.state.merchant?.bulStat}
                                                readOnly={!isMerchantProfileUpdateAllowed()}
                                                className={"form-control"}
                                                type="text"
                                                onChange={(e) => this.updateValue("bulStat", e.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Minimum Transaction Limit</Label>
                                            <ExtendedInputText
                                                type="number"
                                                min="0.01"
                                                step="0.01"
                                                readOnly={!isMerchantProfileUpdateAllowed()}
                                                value={this.state.merchant?.minimumTransactionAmount || 0}
                                                onChange={(value) => this.updateValue("minimumTransactionAmount", value)}
                                                name="Minimum Transaction Limit"
                                                validation={() => (this.state.merchant?.minimumTransactionAmount ?? 0) > 0}
                                                onValidStatusChanged={(value: boolean) =>
                                                    this.validationErrors.set("minimumTransactionAmount", value)
                                                }
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <div className="flex justify-end px-5 py-4">
                                    {isMerchantProfileUpdateAllowed() && (
                                        <Button
                                            type="button"
                                            disabled={Object.values(this.validationErrors).filter((f) => f == false).length > 0}
                                            label="Submit"
                                            icon={<Check size={16} />}
                                            onClick={this.onSubmitButtonClick}
                                        ></Button>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </AppCard.Body>
                </AppCard>
        );
    };

    render() {
        return (
            <>
                <PageLayout heading="Merchant Details" icon={Users}>
                    <div className="space-y-4">
                            {this.renderMerchantBody()}

                                    <MerchantDefaultPaymentMethod merchantId={this.state.merchantId} />

                                    {isSuperAdmin() && (
                                        <ScaSectionsComponent merchantId={this.state.merchantId} merchant={this.state.merchantPass!} />
                                    )}

                                    {isMerchantsClientsCreateAllowed() &&
                                        this.state.merchant &&
                                        (this.state.externalKycLoader ? (
                                            <LoadingDots />
                                        ) : (
                                                <MerchantClientComponent
                                                    merchantId={this.state.merchantId}
                                                    merchant={this.state.merchantPass!}
                                                    isExternalKyc={this.state.isExternalKyc}
                                                    isBtrustAppActive={this.state.isBtrustAppActive}
                                                    isNewRegistrationActive={this.state.isNewRegistrationActive}
                                                    allowMultiMerchantIban={this.state.allowMultiMerchantIban}
                                                    allowMultiMerchantClientIban={this.state.allowMultiMerchantClientIban}
                                                    isKycOptional={this.state.isKycOptional}
                                                    isPepDeclaration={this.state.isPepDeclaration}
                                                    isBillTariffActive={this.state.isBillTariffActive}
                                                    onChangeBillTariffSwitch={async (value) => {
                                                        this.setState({ externalKycLoader: true });
                                                        await this.onToggleBillTariffActiveAsync(value);
                                                        this.setState({ externalKycLoader: false });
                                                    }}
                                                    isCardIssuer={this.state.isCardIssuer}
                                                    onChangeCardIssuerSwitch={async (value) => {
                                                        this.setState({ externalKycLoader: true });
                                                        await this.onToggleCardIssuerAsync(value);
                                                        this.setState({ externalKycLoader: false });
                                                    }}

                                                    onChangePepDeclarationSwitch={async (value) => {
                                                        this.setState({ externalKycLoader: true });
                                                        await this.onChangePepDeclarationSwitch(value);
                                                        this.setState({ externalKycLoader: false });
                                                    }}
                                                    onChangeKycSwitch={async (value) => {
                                                        this.setState({ externalKycLoader: true });
                                                        await this.onChangeKycSwitch(value);
                                                        this.setState({ externalKycLoader: false });
                                                    }}
                                                    onChangeBtrustAppSwitch={async (value) => {
                                                        this.setState({ externalKycLoader: true });
                                                        await this.onBtrustAppSwitchToggle(value);
                                                        this.setState({ externalKycLoader: false });
                                                    }}
                                                    onChangeNewRegistrationSwitch={async (value) => {
                                                        this.setState({ externalKycLoader: true });
                                                        await this.onNewRegistrationSwitchToggle(value);
                                                        this.setState({ externalKycLoader: false });
                                                    }}
                                                    onChangeMultiMerchantIbanSwitch={async (value) => {
                                                        this.setState({ externalKycLoader: true });
                                                        await this.onToggleMerchantMultiIbanAsync(value);
                                                        this.setState({ externalKycLoader: false });
                                                    }}
                                                    onChangeMultiMerchantClientIbanSwitch={async (value) => {
                                                        this.setState({ externalKycLoader: true });
                                                        await this.onToggleMerchantClientMultiIbanAsync(value);
                                                        this.setState({ externalKycLoader: false });
                                                    }}
                                                    onChangeisKycOptionalSwitch={async (value) => {
                                                        this.setState({ externalKycLoader: true });
                                                        await this.onToggleisKycOptionalAsync(value);
                                                        this.setState({ externalKycLoader: false });
                                                    }}
                                                />
                                            ))}

                                    {this.state.merchant && (
                                        <MerchantWalletsComponent
                                            cbsId={this.state.merchantPass?.flexCubeCustomerId}
                                            Id={this.state.merchantId}
                                            walletType={WalletType.Merchant}
                                        />
                                    )}

                                    {this.state.merchantId != 0 && (
                                        <AppCard>
                                            <AppCard.Body className="!p-0">
                                                            <TabView
                                                                scrollable={true}
                                                                style={{ scrollBehavior: "smooth", scrollbarWidth: "auto" }}
                                                                activeIndex={this.state.activeTabIndex}
                                                                onTabChange={(e) => {
                                                                    console.log("in", e.index);
                                                                    this.setState({ activeTabIndex: e.index });
                                                                }}
                                                            >
                                                                <TabPanel
                                                                    header="Integration"
                                                                    pt={{ content: { className: "pt-0 px-5 pb-5" } }}
                                                                    headerTemplate={createTabHeaderTemplate(this.state.activeTabIndex)}
                                                                >
                                                                    <MerchantIntegration merchantId={this.state.merchantId!} />
                                                                </TabPanel>
                                                                <TabPanel
                                                                    header="Contacts"
                                                                    pt={{ content: { className: "pt-0 px-5 pb-5" } }}
                                                                    headerTemplate={createTabHeaderTemplate(this.state.activeTabIndex)}
                                                                >
                                                                    <MerchantContact merchantId={this.state.merchantId!} localMode={false} />
                                                                </TabPanel>
                                                                <TabPanel
                                                                    header="Address"
                                                                    pt={{ content: { className: "pt-0 px-5 pb-5" } }}
                                                                    headerTemplate={createTabHeaderTemplate(this.state.activeTabIndex)}
                                                                >
                                                                    <MerchantAddress merchantId={this.state.merchantId!} localMode={false} />
                                                                </TabPanel>
                                                                <TabPanel
                                                                    header="Groups"
                                                                    pt={{ content: { className: "pt-0 px-5 pb-5" } }}
                                                                    headerTemplate={createTabHeaderTemplate(this.state.activeTabIndex)}
                                                                >
                                                                    <MerchantGroups merchantId={this.state.merchantId!} />
                                                                </TabPanel>
                                                                <TabPanel
                                                                    header="Tariffs"
                                                                    pt={{ content: { className: "pt-0 px-5 pb-5" } }}
                                                                    headerTemplate={createTabHeaderTemplate(this.state.activeTabIndex)}
                                                                >
                                                                    <MerchantTariffs merchantId={this.state.merchantId!.toString()} isBillTariffActive={this.state.isBillTariffActive} />
                                                                </TabPanel>
                                                                <TabPanel
                                                                    header="Transactions"
                                                                    pt={{ content: { className: "pt-0 px-5 pb-5" } }}
                                                                    headerTemplate={createTabHeaderTemplate(this.state.activeTabIndex)}
                                                                >
                                                                    <MerchantTransactions
                                                                        customerId={this.state.merchant?.flexCubeCustomerId}
                                                                        merchantId={this.state.merchantId}
                                                                    />
                                                                </TabPanel>
                                                                <TabPanel
                                                                    header="Customers"
                                                                    pt={{ content: { className: "pt-0 px-5 pb-5" } }}
                                                                    headerTemplate={createTabHeaderTemplate(this.state.activeTabIndex)}
                                                                >
                                                                    <MerchantUsers merchantId={this.state.merchantId!} />
                                                                </TabPanel>
                                                                <TabPanel
                                                                    header="Payment Methods"
                                                                    pt={{ content: { className: "pt-0 px-5 pb-5" } }}
                                                                    headerTemplate={createTabHeaderTemplate(this.state.activeTabIndex)}
                                                                >
                                                                    <MerchantPaymentMethods merchantId={this.state.merchantId!} viewOnly={false} />
                                                                </TabPanel>
                                                                <TabPanel
                                                                    header="Documents"
                                                                    pt={{ content: { className: "pt-0 px-5 pb-5" } }}
                                                                    headerTemplate={createTabHeaderTemplate(this.state.activeTabIndex)}
                                                                >
                                                                    <MerchantDocuments merchantId={this.state.merchantId!} localMode={false} />
                                                                </TabPanel>
                                                                {this.state.merchantId && this.state.merchant?.hasMerchantVendors && (
                                                                    <TabPanel
                                                                        header="Utility Vendors"
                                                                        pt={{ content: { className: "pt-0 px-5 pb-5" } }}
                                                                        headerTemplate={createTabHeaderTemplate(this.state.activeTabIndex)}
                                                                    >
                                                                        <MerchantVendors merchantId={this.state.merchantId} />
                                                                    </TabPanel>
                                                                )}
                                                                {this.state.merchantId && (
                                                                    <TabPanel
                                                                        header="Merchant Configration"
                                                                        style={{ paddingRight: "50px" }}
                                                                        pt={{ content: { className: "pt-0 px-5 pb-5" } }}
                                                                        headerTemplate={createTabHeaderTemplate(this.state.activeTabIndex)}
                                                                    >
                                                                        <MerchantConfigration merchantId={this.state.merchantId} isExternalKyc={this.state.isExternalKyc} />
                                                                    </TabPanel>
                                                                )}
                                                            </TabView>
                                            </AppCard.Body>
                                        </AppCard>
                                    )}
                    </div>
                </PageLayout>
            </>
        );
    }
}
